import type { SluggedId } from "Models";
import type { AdKitKey } from "types/enums";
import type { ClubhouseAdKitDto } from "entities/responses/adkit";
import type {
  CreateAdKitLocationDto,
  CreateAdKitLocationInstanceDto,
} from "entities/requests/adkit";
import type { PlacementCatalogEntry } from "../../placementCatalog";
import {
  draftFromCampaign,
  enabledHolesMap,
  locationPayload,
  placementInstances,
  type CampaignDraft,
  type HoleLoadTiming,
  type PlacementSetup,
} from "./campaignDraft";

/**
 * One API call that saving an edited campaign makes (CLUB-2657). Only what
 * changed is sent, so editing one placement never touches another.
 */
export type CampaignEdit =
  | { kind: "updateTiming"; timing: HoleLoadTiming }
  | { kind: "addLocations"; body: CreateAdKitLocationDto[] }
  | { kind: "deleteLocation"; key: AdKitKey; locationId: SluggedId }
  | {
      kind: "updateHoles";
      key: AdKitKey;
      locationId: SluggedId;
      enabledHoles: NonNullable<CreateAdKitLocationDto["enabledHoles"]>;
    }
  | {
      kind: "deleteInstance";
      key: AdKitKey;
      locationId: SluggedId;
      instanceId: SluggedId;
    }
  | {
      kind: "reorderInstances";
      key: AdKitKey;
      locationId: SluggedId;
      order: { id: SluggedId; orderLocation: number }[];
    }
  | {
      kind: "addInstances";
      key: AdKitKey;
      locationId: SluggedId;
      body: CreateAdKitLocationInstanceDto[];
    };

/** The campaign as edit mode opens it: its own name, saved in place. */
export function editDraftFromCampaign(
  campaign: Parameters<typeof draftFromCampaign>[0],
  catalog: PlacementCatalogEntry[],
): CampaignDraft {
  return {
    ...draftFromCampaign(campaign, catalog),
    source: { kind: "edit", campaignId: campaign.id },
    name: campaign.name,
  };
}

const sameJson = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);
const sameHoles = (a: number[] = [], b: number[] = []) =>
  sameJson(
    [...a].sort((x, y) => x - y),
    [...b].sort((x, y) => x - y),
  );
const sameTiming = (a?: HoleLoadTiming, b?: HoleLoadTiming) =>
  a?.fadeIn === b?.fadeIn &&
  a?.display === b?.display &&
  a?.fadeOut === b?.fadeOut;

type CatalogFields = Pick<
  PlacementCatalogEntry,
  "key" | "isHoleEnabled" | "hasTransitions"
>;

function instanceEdits(
  key: AdKitKey,
  location: NonNullable<ClubhouseAdKitDto["adLocations"]>[number],
  setup: PlacementSetup,
): CampaignEdit[] {
  const existing = (location.adInstances ?? [])
    .filter((i) => !i.deleted)
    .sort((a, b) => (a.orderLocation ?? 0) - (b.orderLocation ?? 0));
  const wanted = placementInstances(setup);

  // An image on a hole (0 for "every hole") is the same instance before and
  // after; anything else is removed and added. Positions count from 1: the
  // API ignores an update to orderLocation 0 (it reads as "unchanged"), so a
  // 0-based renumber could never move an image to the top.
  const unmatched = [...existing];
  const order: { id: SluggedId; orderLocation: number }[] = [];
  const added: CreateAdKitLocationInstanceDto[] = [];
  wanted.forEach((want, index) => {
    const position = index + 1;
    const at = unmatched.findIndex(
      (i) => i.imageId === want.imageId && i.holeNumber === want.holeNumber,
    );
    const match = at >= 0 ? unmatched.splice(at, 1)[0] : undefined;
    if (!match) {
      added.push({
        ...want,
        overrideWarnings: true,
        orderLocation: position,
        widthAdjustment: -1,
        heightAdjustment: -1,
      });
    } else if (match.orderLocation !== position) {
      order.push({ id: match.id, orderLocation: position });
    }
  });

  const locationId = location.id;
  return [
    ...unmatched.map(
      (i): CampaignEdit => ({
        kind: "deleteInstance",
        key,
        locationId,
        instanceId: i.id,
      }),
    ),
    ...(order.length > 0
      ? [{ kind: "reorderInstances", key, locationId, order } as const]
      : []),
    ...(added.length > 0
      ? [{ kind: "addInstances", key, locationId, body: added } as const]
      : []),
  ];
}

/**
 * The calls that turn `campaign` into `draft`. Placements are compared with
 * the draft the campaign opened as, so one the operator didn't touch sends
 * nothing even if the API holds it in a shape setup would write differently.
 * Removals go first so positions are free before reorders and adds.
 */
export function planCampaignEdits(
  campaign: Pick<
    ClubhouseAdKitDto,
    "id" | "name" | "description" | "adLocations" | "transition"
  >,
  draft: CampaignDraft,
  catalog: CatalogFields[],
): CampaignEdit[] {
  const byKey = new Map(catalog.map((p) => [p.key, p]));
  const original = draftFromCampaign(
    campaign,
    catalog as PlacementCatalogEntry[],
  );
  const locationByKey = new Map(
    (campaign.adLocations ?? [])
      .filter((l) => !l.deleted)
      .map((l) => [l.adKitMetaDataId, l]),
  );

  const edits: CampaignEdit[] = [];
  for (const key of original.placements) {
    const location = locationByKey.get(key);
    if (location && !draft.placements.includes(key)) {
      edits.push({ kind: "deleteLocation", key, locationId: location.id });
    }
  }

  const addedLocations: CreateAdKitLocationDto[] = [];
  for (const key of draft.placements) {
    const placement = byKey.get(key);
    const setup = draft.setup[key];
    if (!placement || !setup) continue;
    const location = locationByKey.get(key);
    if (!location) {
      addedLocations.push(locationPayload(key, setup, placement));
      continue;
    }
    const before = original.setup[key];
    if (before && sameJson(before, setup)) continue;
    if (placement.isHoleEnabled && !sameHoles(before?.holes, setup.holes)) {
      edits.push({
        kind: "updateHoles",
        key,
        locationId: location.id,
        enabledHoles: enabledHolesMap(setup.holes),
      });
    }
    if (
      !before ||
      !sameJson(placementInstances(before), placementInstances(setup))
    ) {
      edits.push(...instanceEdits(key, location, setup));
    }
  }

  // Deletes first, then everything else in the order it was planned.
  edits.sort(
    (a, b) =>
      Number(b.kind.startsWith("delete")) - Number(a.kind.startsWith("delete")),
  );
  if (addedLocations.length > 0) {
    edits.push({ kind: "addLocations", body: addedLocations });
  }

  const timed = draft.placements.some((key) => byKey.get(key)?.hasTransitions);
  if (timed && draft.timing && !sameTiming(draft.timing, original.timing)) {
    edits.push({ kind: "updateTiming", timing: draft.timing });
  }
  return edits;
}
